import { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { responsive } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import AboutScreenReader from 'hb-react/frontoffice/apps/LiveFeed/components/AboutScreenReader';
import { useLandingContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LandingContext';
import UserEventModal from 'hb-react/frontoffice/components/UserEventModal';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventCanceledLabel from 'hb-react/shared/apps/Events/components/EventCanceledLabel';
import Attendees from 'hb-react/shared/components/DesignSystem/Attendees';
import useEventDisplayTimeZone from 'hb-react/shared/components/EventDate/hooks/useEventDisplayTimeZone';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import { participantThresholdMet } from 'helpers/participantThresholdHelper';

import Landing from '../Landing';

const UserEvent = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { id, location, logo, startDate, timezone, title, cancelled } =
    useLandingContext();
  const isMobileDevice = useIsMobileDevice();
  const { currentUser } = useDefaultAppContexts();
  const eventDisplayTimeZone = useEventDisplayTimeZone(timezone);

  const handleOpenModal = useCallback(() => {
    setModalIsOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const dateStr = useMemo(
    () =>
      getDateStr({
        startDate,
        endDate: startDate,
        timezone: eventDisplayTimeZone,
      }),
    [startDate, eventDisplayTimeZone],
  );

  return (
    <Landing
      title={s__('LiveFeed|created an event <strong>%{title}</strong>').i__({
        title,
      })}
    >
      <Wrapper>
        <Box>
          <Box position="relative">
            <Box position="absolute" top={2} left={2}>
              <EventCanceledLabel event={{ cancelled }} />
            </Box>
            <Box opacity={cancelled ? 0.4 : 1}>
              <WrapperEventImage src={logo.preview_url} />
            </Box>
          </Box>
          <Box
            flex={1}
            flexDirection="column"
            pl={{ _: 4, m: 6 }}
            pr={{ _: 0, m: 4 }}
            py={{ _: 0, m: 3 }}
          >
            <Box flexDirection="column">
              <Box font="h4-semibold" title={title}>
                <Dotdotdot clamp={1}>{title}</Dotdotdot>
              </Box>
              <Box mt={1}>{dateStr}</Box>
              <Dotdotdot clamp={1}>{location?.address}</Dotdotdot>
              <Box display="block" mt={3}>
                <Button onClick={handleOpenModal}>
                  {__('Find out more')}
                  <AboutScreenReader name={title} />
                </Button>
              </Box>
            </Box>

            {!isMobileDevice && <EventAttendees eventId={id} />}
          </Box>
        </Box>
        {isMobileDevice && <EventAttendees eventId={id} />}
      </Wrapper>
      {modalIsOpen && (
        <UserEventModal
          eventId={id}
          userId={currentUser.id}
          onCloseModal={handleCloseModal}
        />
      )}
    </Landing>
  );
};

export default UserEvent;

export const EventAttendees = ({ eventId }) => {
  const {
    attendees,
    participantDisplayThreshold,
    totalAttendees,
    totalExternalAttendees,
    totalVisibleAttendees,
    totalVisibleYesResponses,
    originalType,
  } = useLandingContext();

  if (
    !participantThresholdMet({
      threshold: participantDisplayThreshold,
      attendees: totalVisibleAttendees,
      responses: totalVisibleYesResponses,
    })
  ) {
    return null;
  }

  return (
    <Box display="block">
      {attendees?.length > 0 && (
        <Box mt={{ _: 4, m: 3 }} data-testid="event-attendees">
          <Attendees
            attendees={attendees}
            totalAttendees={totalAttendees}
            totalExternalAttendees={totalExternalAttendees}
            eventId={eventId}
            maxAvatars={useIsMobileDevice ? 4 : 5}
            eventType={originalType}
          />
        </Box>
      )}
    </Box>
  );
};

export const Wrapper = styled(Box).attrs(() => ({
  display: 'block',
  bg: 'smoke',
  mt: 4,
  mx: { _: -4, m: 0 },
  p: { _: 4, m: 0 },
}))`
  border-radius: 2px;
`;
export const WrapperEventImage = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['src'].includes(prop),
  })
  .attrs(({ src }) => ({
    style: {
      backgroundImage: `url(${src})`,
    },
    height: { _: 102, m: 200 },
    width: { _: 102, m: 200 },
  }))`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 2px;

  ${responsive('m')`
    border-radius: 0;
    border-top-left-radius: 2px;
    border-bottom-left-radius: 2px;
  `}
`;
